// Copied verbatim from dashboard/src/lib/format.ts (separate package, no shared imports). Keep in sync.
/**
 * Presentation helpers.
 *
 * Money crosses the wire as a decimal *string* and stays a string until the
 * moment it is drawn. Nothing in here calls `parseFloat`/`Number` on an amount
 * and nothing does arithmetic on one: rounding and grouping are done on the
 * digits themselves (with `BigInt` for the integer part), so a value that
 * Postgres stored as DECIMAL(20,8) survives the trip to the screen intact.
 */

import { isIsoCurrencyCode, minorUnits, USDC } from './currencies';

/** Shown wherever a value is genuinely unknown. Never substitute a zero. */
export const NO_DATA = '—';

interface DecimalParts {
  negative: boolean;
  /** Integer digits, no sign, no leading zeros (except a lone "0"). */
  int: string;
  /** Fractional digits, no decimal point. */
  frac: string;
}

const DECIMAL_PATTERN = /^([+-]?)(\d*)(?:\.(\d*))?$/;

function parseDecimal(value: string): DecimalParts | null {
  const match = DECIMAL_PATTERN.exec(value.trim());
  if (!match) return null;

  const int = match[2] ?? '';
  const frac = match[3] ?? '';
  if (int === '' && frac === '') return null;

  return {
    negative: match[1] === '-',
    int: stripLeadingZeros(int === '' ? '0' : int),
    frac,
  };
}

function stripLeadingZeros(digits: string): string {
  const stripped = digits.replace(/^0+(?=\d)/, '');
  return stripped === '' ? '0' : stripped;
}

/** Adds 1 to a string of digits, carrying into a new leading digit if needed. */
function incrementDigits(digits: string): string {
  const out = digits.split('');
  let i = out.length - 1;
  for (; i >= 0; i -= 1) {
    if (out[i] === '9') {
      out[i] = '0';
    } else {
      out[i] = String(Number(out[i]) + 1);
      break;
    }
  }
  if (i < 0) out.unshift('1');
  return out.join('');
}

/** Half-up rounding to `digits` fraction digits, done on the digits alone. */
function roundToDigits(parts: DecimalParts, digits: number): DecimalParts {
  if (parts.frac.length <= digits) {
    return { ...parts, frac: parts.frac.padEnd(digits, '0') };
  }

  let int = parts.int;
  let frac = parts.frac.slice(0, digits);
  const nextDigit = parts.frac.charCodeAt(digits) - 48;

  if (nextDigit >= 5) {
    const carried = incrementDigits(int + frac);
    if (digits === 0) {
      int = carried;
      frac = '';
    } else {
      int = carried.slice(0, carried.length - digits);
      frac = carried.slice(carried.length - digits);
    }
    if (int === '') int = '0';
  }

  return { negative: parts.negative, int: stripLeadingZeros(int), frac };
}

function isZeroParts(parts: DecimalParts): boolean {
  return !/[1-9]/.test(parts.int + parts.frac);
}

/**
 * Renders the digits with locale grouping. The integer part goes through
 * `BigInt`, which is exact for any length; the fraction is appended verbatim.
 */
function renderParts(
  parts: DecimalParts,
  locale: string | undefined,
  includeSign = true
): string {
  let grouped: string;
  try {
    grouped = new Intl.NumberFormat(locale).format(BigInt(parts.int));
  } catch {
    grouped = parts.int;
  }

  const decimalSeparator =
    new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === 'decimal')?.value ??
    '.';

  const body = parts.frac ? `${grouped}${decimalSeparator}${parts.frac}` : grouped;
  // Avoid rendering "-0.00".
  const sign = includeSign && parts.negative && !isZeroParts(parts) ? '-' : '';
  return `${sign}${body}`;
}

const NUMERIC_PART_TYPES = new Set([
  'integer',
  'group',
  'decimal',
  'fraction',
  'minusSign',
  'plusSign',
]);

/**
 * Wraps an already-rendered number in the locale's currency decoration
 * (symbol, placement, spacing) without ever handing the amount to a float.
 */
function decorateWithCurrency(
  body: string,
  currency: string,
  locale: string | undefined,
  digits: number,
  negative: boolean
): string {
  const code = currency.trim().toUpperCase();
  const plain = negative ? `-${body}` : body;
  if (!isIsoCurrencyCode(code)) {
    // USDC and other non-ISO tickers: Intl would throw on these.
    return `${plain} ${code}`;
  }

  let template: Intl.NumberFormatPart[];
  try {
    // The sample's sign matters: it puts the minus (or the accounting
    // parentheses) where this locale expects it, e.g. "-$42.13", not "$-42.13".
    template = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).formatToParts(negative ? -1 : 0);
  } catch {
    return `${plain} ${code}`;
  }

  let out = '';
  let inserted = false;
  for (const part of template) {
    if (part.type === 'minusSign' || part.type === 'plusSign') {
      out += part.value;
      continue;
    }
    if (NUMERIC_PART_TYPES.has(part.type)) {
      if (!inserted) {
        out += body;
        inserted = true;
      }
      continue;
    }
    out += part.value;
  }
  return inserted ? out : `${out}${body}`;
}

/**
 * Formats a decimal *string* as money in its own currency, using that
 * currency's real minor-unit exponent (0 for JPY, 3 for KWD, ...).
 * Returns `NO_DATA` for a missing or malformed amount rather than "0.00".
 */
export function formatMoney(
  value: string | null | undefined,
  currency: string,
  locale?: string
): string {
  if (value === null || value === undefined || value === '') return NO_DATA;

  const parsed = parseDecimal(value);
  if (!parsed) return NO_DATA;

  const digits = minorUnits(currency);
  const rounded = roundToDigits(parsed, digits);
  // Rounding can turn -0.001 into zero; only then is the sign dropped.
  const negative = rounded.negative && !isZeroParts(rounded);
  return decorateWithCurrency(
    renderParts(rounded, locale, false),
    currency,
    locale,
    digits,
    negative
  );
}

/** Formats a USDC decimal string, e.g. "1,234.56 USDC". */
export function formatUsdc(value: string | null | undefined, locale?: string): string {
  return formatMoney(value, USDC, locale);
}

/** Grouped plain number from a decimal string (no currency decoration). */
export function formatDecimal(
  value: string | null | undefined,
  digits: number,
  locale?: string
): string {
  if (value === null || value === undefined || value === '') return NO_DATA;
  const parsed = parseDecimal(value);
  if (!parsed) return NO_DATA;
  return renderParts(roundToDigits(parsed, digits), locale);
}

/** True when the string parses to a non-zero amount. */
export function isNonZeroAmount(value: string | null | undefined): boolean {
  if (value === null || value === undefined || value === '') return false;
  const parsed = parseDecimal(value);
  return parsed !== null && !isZeroParts(parsed);
}

/**
 * Turns a rate held as a decimal string ("0.0100") into a percentage
 * ("1.00%") by shifting the decimal point — no multiplication by 100.
 */
export function formatRateAsPercent(
  value: string | null | undefined,
  digits = 2,
  locale?: string
): string {
  if (value === null || value === undefined || value === '') return NO_DATA;
  const parsed = parseDecimal(value);
  if (!parsed) return NO_DATA;

  const frac = parsed.frac.padEnd(2, '0');
  const shifted: DecimalParts = {
    negative: parsed.negative,
    int: stripLeadingZeros(parsed.int + frac.slice(0, 2)),
    frac: frac.slice(2),
  };
  return `${renderParts(roundToDigits(shifted, digits), locale)}%`;
}

/**
 * `part / whole` as a percentage ("1.23%"), for two decimal strings such as a
 * fee total over a volume. Done in BigInt on the digits, like everything else
 * here, so neither amount passes through a float. A zero or missing `whole`
 * gives `NO_DATA`: a rate over nothing is unknown, not 0%.
 */
export function formatRatioAsPercent(
  part: string | null | undefined,
  whole: string | null | undefined,
  digits = 2,
  locale?: string
): string {
  if (!part || !whole) return NO_DATA;
  const p = parseDecimal(part);
  const w = parseDecimal(whole);
  if (!p || !w) return NO_DATA;

  const scale = Math.max(p.frac.length, w.frac.length);
  const scaled = (x: DecimalParts) => BigInt(x.int + x.frac.padEnd(scale, '0'));
  const num = scaled(p);
  const den = scaled(w);
  if (den === 0n) return NO_DATA;

  // x100 for the percentage, x10^digits for the fraction, then half-up.
  const factor = 10n ** BigInt(digits + 2);
  const q = (num * factor * 2n + den) / (2n * den);
  const s = q.toString().padStart(digits + 1, '0');
  const result: DecimalParts = {
    negative: p.negative !== w.negative,
    int: stripLeadingZeros(s.slice(0, s.length - digits)),
    frac: digits > 0 ? s.slice(s.length - digits) : '',
  };
  return `${renderParts(result, locale)}%`;
}

/**
 * A period-over-period delta. These are percentages the API computed, not
 * money, so a JS number is the right carrier — but `null` means "not
 * computable" and must be shown as such, never as 0%.
 */
export function formatSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_DATA;
  const sign = value > 0 ? '+' : '';
  return `${sign}${value}%`;
}

/** Seconds, or `NO_DATA` when nothing has settled yet. */
export function formatSeconds(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NO_DATA;
  return `${value}s`;
}

/**
 * Presentation-only: charts need a coordinate, and a pixel position cannot be
 * a decimal string. The exact string is kept alongside it and is what the
 * tooltip and every text label render — this number is never displayed.
 */
export function toChartValue(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function truncateWallet(address: string, chars = 6): string {
  if (!address || address.length < chars * 2 + 4) return address;
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return NO_DATA;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_DATA;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return NO_DATA;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return NO_DATA;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
